/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  TextField,
  Grid,
  Link,
} from '@material-ui/core';
import {
  forgotPassword,
  forgotPasswordSubmit,
  selectIsRecoveryCodeSent,
  selectIsPending,
  selectIsForgotPassword,
  selectIsPasswordReset,
  setIsPasswordReset,
} from '../../store/authSlice';
import {
  isRequired,
  passwordValidationRules,
  validateField,
  validateForm,
  noPasswordMismatch,
} from '../../helpers/validation';
import PasswordField from '../common/PasswordField';

const useStyles = makeStyles((theme) => ({
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  elementHidden: {
    display: 'none',
  },
}));

export default function ForgotPasswordForm() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const isPending = useSelector(selectIsPending);
  const isForgotPassword = useSelector(selectIsForgotPassword);
  const isRecoveryCodeSent = useSelector(selectIsRecoveryCodeSent);
  const isPasswordReset = useSelector(selectIsPasswordReset);
  const [formErrors, setFormErrors] = useState({});
  const [formValues, setFormValues] = useState({
    username: '',
    recoveryCode: '',
    newPassword: '',
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mismatchError, setMismatchError] = useState('');

  const formRules = {
    username: [isRequired],
    recoveryCode: [isRequired],
    newPassword: [...passwordValidationRules],
  };

  const handleChange = (event) => {
    setFormValues({ ...formValues, [event.target.name]: event.target.value });
  };

  const handleBlur = (event) => {
    validateField(formRules, formErrors, setFormErrors, event.target.name, event.target.value);
  };

  const handleConfirmPasswordChange = (event) => {
    setConfirmPassword(event.target.value);
  };

  const handleIsRecoveryCodeSentClick = (event) => {
    event.preventDefault();
    dispatch(setIsPasswordReset(true));
  };

  const validateNewPassword = () => {
    if (!isRequired.validate(confirmPassword)) {
      setMismatchError(isRequired.error);
      return false;
    }

    if (!noPasswordMismatch(formValues.newPassword).validate(confirmPassword)) {
      setMismatchError(noPasswordMismatch(formValues.newPassword).error);
      return false;
    }

    setMismatchError('');
    return true;
  };

  const attemptForgotPassword = (event) => {
    event.preventDefault();
    if (validateForm(formRules, { username: formValues.username }, formErrors, setFormErrors)) {
      dispatch(forgotPassword(formValues.username.trim()));
    }
  };

  const attemptForgotPasswordSubmit = (event) => {
    event.preventDefault();
    if (validateForm(formRules, formValues, formErrors, setFormErrors) && validateNewPassword()) {
      dispatch(forgotPasswordSubmit({
        username: formValues.username.trim(),
        recoveryCode: formValues.recoveryCode,
        newPassword: formValues.newPassword,
      }));
    }
  };

  // Reset values when form is displayed
  useEffect(() => {
    if (isForgotPassword) {
      setMismatchError('');
      setConfirmPassword('');
      setFormValues({
        username: '',
        recoveryCode: '',
        newPassword: '',
      });
    }
  }, [isForgotPassword]);

  return (
    <>
      <div className={clsx((isRecoveryCodeSent || isPasswordReset) && classes.elementHidden)}>
        <form className={classes.form} noValidate>
          <TextField
            variant="outlined"
            margin="normal"
            id="forgot-username"
            label="Username"
            name="username"
            autoComplete="username"
            value={formValues.username}
            error={!!formErrors.username}
            helperText={formErrors.username}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isPending}
            fullWidth
            required
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            className={classes.submit}
            onClick={attemptForgotPassword}
            fullWidth
          >
            Send Code
          </Button>
        </form>
        <Grid container>
          <Grid item xs>
            <Link component="button" onClick={handleIsRecoveryCodeSentClick} variant="body2">
              Already have a code?
            </Link>
          </Grid>
        </Grid>
      </div>
      <div className={clsx(!(isRecoveryCodeSent || isPasswordReset) && classes.elementHidden)}>
        <form className={classes.form} noValidate>
          <TextField
            variant="outlined"
            margin="normal"
            id="forgot-username"
            label="Username"
            name="username"
            autoComplete="username"
            className={clsx(!isPasswordReset && classes.elementHidden)}
            value={formValues.username}
            error={!!formErrors.username}
            helperText={formErrors.username}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isPending}
            fullWidth
            required
          />
          <TextField
            variant="outlined"
            margin="normal"
            id="recovery-code"
            label="Recovery Code"
            name="recoveryCode"
            autoComplete="one-time-code"
            value={formValues.recoveryCode}
            error={!!formErrors.recoveryCode}
            helperText={formErrors.recoveryCode}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isPending}
            fullWidth
            required
          />
          <PasswordField
            variant="outlined"
            margin="normal"
            name="newPassword"
            label="New Password"
            id="forgot-new-password"
            autoComplete="new-password"
            value={formValues.newPassword}
            error={!!formErrors.newPassword}
            helperText={formErrors.newPassword}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isPending}
            fullWidth
            required
          />
          <PasswordField
            variant="outlined"
            margin="normal"
            name="confirmPassword"
            label="Confirm Password"
            id="forgot-confirm-password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            onBlur={validateNewPassword}
            error={!!mismatchError}
            helperText={mismatchError}
            disabled={isPending}
            fullWidth
            required
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isPending}
            className={classes.submit}
            onClick={attemptForgotPasswordSubmit}
            fullWidth
          >
            Update Password
          </Button>
        </form>
      </div>
    </>
  );
}
